import { useEffect, useState, useCallback } from 'react';
import { 
  Package, Upload, Download, RefreshCw, History, CheckCircle, 
  XCircle, AlertTriangle, Search, FileSpreadsheet, BarChart3,
  ArrowUpDown, Clock, User, RotateCcw, Edit2, Save, X, Filter
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminInventorySync = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [eanList, setEanList] = useState([]);
  const [history, setHistory] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [eanFilter, setEanFilter] = useState('all');
  
  // Import state
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  
  // EAN edit state
  const [editingEan, setEditingEan] = useState(null);
  const [editEanValue, setEditEanValue] = useState('');

  const getHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('admin_token')}`
  });

  const loadDashboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/inventory-sync/dashboard`, { headers: getHeaders() });
      setDashboard(response.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Failed to load dashboard');
    }
  }, []);

  const loadEanList = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (eanFilter !== 'all') params.append('filter_type', eanFilter);
      
      const response = await axios.get(`${API}/admin/inventory-sync/ean-list?${params}`, { headers: getHeaders() });
      setEanList(response.data.items || []);
    } catch (error) {
      console.error('Error loading EAN list:', error);
      toast.error('Failed to load EAN list');
    }
  }, [searchTerm, eanFilter]);

  const loadHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/inventory-sync/history`, { headers: getHeaders() });
      setHistory(response.data.history || []);
    } catch (error) {
      console.error('Error loading history:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([loadDashboard(), loadEanList(), loadHistory()]);
      setLoading(false);
    };
    loadData();
  }, [loadDashboard, loadEanList, loadHistory]);

  useEffect(() => {
    if (activeTab === 'ean-management') {
      loadEanList();
    }
  }, [activeTab, searchTerm, eanFilter, loadEanList]);

  // Handle file upload for import
  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }
    
    setImportFile(file);
    setImporting(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/admin/inventory-sync/validate-import`, formData, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      
      setImportPreview(response.data);
    } catch (error) {
      console.error('Error validating file:', error);
      toast.error(error.response?.data?.detail || 'Failed to validate file');
      setImportFile(null);
    } finally {
      setImporting(false);
    }
  };

  // Process import
  const processImport = async (skipWarnings = false) => {
    if (!importPreview) return;
    
    setImporting(true);
    try {
      const records = [...importPreview.all_valid, ...importPreview.all_invalid];
      
      const response = await axios.post(`${API}/admin/inventory-sync/process-import`, records, {
        headers: getHeaders(),
        params: { 
          file_name: importFile?.name || 'import',
          skip_warnings: skipWarnings 
        }
      });
      
      toast.success(`Import complete! ${response.data.successful_records} records updated.`);
      setImportFile(null);
      setImportPreview(null);
      loadDashboard();
      loadHistory();
      loadEanList();
    } catch (error) {
      console.error('Error processing import:', error);
      toast.error(error.response?.data?.detail || 'Failed to process import');
    } finally {
      setImporting(false);
    }
  };

  // Update EAN
  const handleUpdateEan = async (productId, variantId) => {
    try {
      const formData = new FormData();
      formData.append('product_id', productId);
      formData.append('variant_id', variantId);
      formData.append('ean', editEanValue);
      
      await axios.post(`${API}/admin/inventory-sync/update-ean`, formData, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('EAN updated successfully');
      setEditingEan(null);
      setEditEanValue('');
      loadEanList();
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update EAN');
    }
  };

  // Download exports
  const downloadMasterInventory = async () => {
    try {
      const response = await axios.get(`${API}/admin/inventory-sync/export-master`, {
        headers: getHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `master_inventory_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Master inventory exported');
    } catch (error) {
      toast.error('Failed to export inventory');
    }
  };

  const downloadOnlineSales = async () => {
    try {
      const response = await axios.get(`${API}/admin/inventory-sync/export-online-sales`, {
        headers: getHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `online_sales_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Online sales exported');
      loadHistory();
    } catch (error) {
      toast.error('Failed to export sales');
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/admin/inventory-sync/download-template`, {
        headers: getHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'vyapar_import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  // Rollback sync
  const handleRollback = async (syncId) => {
    if (!window.confirm('Are you sure you want to rollback this sync? Stock will be restored to previous values.')) {
      return;
    }
    
    try {
      await axios.post(`${API}/admin/inventory-sync/rollback/${syncId}`, {}, { headers: getHeaders() });
      toast.success('Sync rolled back successfully');
      loadHistory();
      loadDashboard();
      loadEanList();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to rollback sync');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-gold" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-light text-charcoal">Inventory Sync</h1>
          <p className="text-charcoal-light mt-1">EAN-based inventory synchronization with Vyapar</p>
        </div>
        <button
          onClick={() => { loadDashboard(); loadEanList(); loadHistory(); }}
          className="inline-flex items-center gap-2 px-4 py-2 border border-charcoal/20 text-charcoal hover:bg-beige"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-charcoal/10">
        <div className="flex gap-6">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
            { id: 'import', label: 'Vyapar Import', icon: Upload },
            { id: 'export', label: 'Export Sales', icon: Download },
            { id: 'ean-management', label: 'EAN Management', icon: Package },
            { id: 'history', label: 'Sync History', icon: History }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 py-4 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === tab.id
                  ? 'border-gold text-charcoal'
                  : 'border-transparent text-charcoal-light hover:text-charcoal'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && dashboard && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white border border-charcoal/10 p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 flex items-center justify-center">
                  <Upload className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-charcoal">{dashboard.today_offline_sales_imported}</p>
                  <p className="text-xs text-charcoal-light">Offline Sales Imported</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white border border-charcoal/10 p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 flex items-center justify-center">
                  <Download className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-charcoal">{dashboard.today_online_sales_exported}</p>
                  <p className="text-xs text-charcoal-light">Online Sales Exported</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white border border-charcoal/10 p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gold/20 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-charcoal">{dashboard.products_updated}</p>
                  <p className="text-xs text-charcoal-light">Products Updated Today</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white border border-charcoal/10 p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-red-100 flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-charcoal">{dashboard.failed_records}</p>
                  <p className="text-xs text-charcoal-light">Failed Records</p>
                </div>
              </div>
            </div>
          </div>

          {/* Last Sync & EAN Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border border-charcoal/10 p-6">
              <h3 className="font-medium text-charcoal mb-4">Last Sync</h3>
              {dashboard.last_sync_time ? (
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-charcoal-light" />
                  <span className="text-charcoal">{formatDate(dashboard.last_sync_time)}</span>
                </div>
              ) : (
                <p className="text-charcoal-light">No syncs yet</p>
              )}
              {dashboard.pending_syncs > 0 && (
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 text-sm text-yellow-700">
                  {dashboard.pending_syncs} sync(s) in progress...
                </div>
              )}
            </div>
            
            <div className="bg-white border border-charcoal/10 p-6">
              <h3 className="font-medium text-charcoal mb-4">EAN Status</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-charcoal-light">Total Variants</span>
                  <span className="font-medium text-charcoal">{dashboard.total_variants}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-charcoal-light">With EAN</span>
                  <span className="font-medium text-green-600">{dashboard.total_variants - dashboard.variants_without_ean}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-charcoal-light">Without EAN</span>
                  <span className="font-medium text-red-600">{dashboard.variants_without_ean}</span>
                </div>
              </div>
              {dashboard.variants_without_ean > 0 && (
                <button
                  onClick={() => { setActiveTab('ean-management'); setEanFilter('without_ean'); }}
                  className="mt-4 text-sm text-gold hover:underline"
                >
                  View variants without EAN →
                </button>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white border border-charcoal/10 p-6">
            <h3 className="font-medium text-charcoal mb-4">Quick Actions</h3>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setActiveTab('import')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gold text-white text-sm font-medium hover:bg-gold/90"
              >
                <Upload className="w-4 h-4" />
                Import Vyapar Sales
              </button>
              <button
                onClick={downloadOnlineSales}
                className="inline-flex items-center gap-2 px-4 py-2 bg-charcoal text-ivory text-sm font-medium hover:bg-charcoal/90"
              >
                <Download className="w-4 h-4" />
                Export Online Sales
              </button>
              <button
                onClick={downloadMasterInventory}
                className="inline-flex items-center gap-2 px-4 py-2 border border-charcoal/20 text-charcoal text-sm font-medium hover:bg-beige"
              >
                <FileSpreadsheet className="w-4 h-4" />
                Export Master Inventory
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Tab */}
      {activeTab === 'import' && (
        <div className="space-y-6">
          {!importPreview ? (
            <div className="bg-white border border-charcoal/10 p-8">
              <div className="max-w-xl mx-auto text-center">
                <Upload className="w-12 h-12 mx-auto mb-4 text-charcoal-light" />
                <h3 className="text-lg font-medium text-charcoal mb-2">Import Vyapar Sales</h3>
                <p className="text-charcoal-light text-sm mb-6">
                  Upload an Excel file with EAN and Quantity Sold columns to update stock.
                </p>
                
                <div className="border-2 border-dashed border-charcoal/20 p-8 mb-4 hover:border-gold transition-colors">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="import-file"
                    disabled={importing}
                  />
                  <label htmlFor="import-file" className="cursor-pointer">
                    <FileSpreadsheet className="w-8 h-8 mx-auto mb-3 text-charcoal-light" />
                    <p className="text-sm text-charcoal">
                      {importing ? 'Processing...' : 'Click to select Excel file'}
                    </p>
                    <p className="text-xs text-charcoal-light mt-1">
                      Supports .xlsx and .xls files
                    </p>
                  </label>
                </div>
                
                <button
                  onClick={downloadTemplate}
                  className="text-sm text-gold hover:underline"
                >
                  Download Sample Template
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Import Summary */}
              <div className="bg-white border border-charcoal/10 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium text-charcoal">Import Preview: {importPreview.file_name}</h3>
                  <button
                    onClick={() => { setImportFile(null); setImportPreview(null); }}
                    className="text-charcoal-light hover:text-charcoal"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <div className="text-center p-3 bg-beige">
                    <p className="text-2xl font-bold text-charcoal">{importPreview.total_records}</p>
                    <p className="text-xs text-charcoal-light">Total Records</p>
                  </div>
                  <div className="text-center p-3 bg-green-50">
                    <p className="text-2xl font-bold text-green-600">{importPreview.valid_records}</p>
                    <p className="text-xs text-charcoal-light">Valid</p>
                  </div>
                  <div className="text-center p-3 bg-yellow-50">
                    <p className="text-2xl font-bold text-yellow-600">{importPreview.warning_records}</p>
                    <p className="text-xs text-charcoal-light">Warnings</p>
                  </div>
                  <div className="text-center p-3 bg-red-50">
                    <p className="text-2xl font-bold text-red-600">{importPreview.invalid_records}</p>
                    <p className="text-xs text-charcoal-light">Invalid</p>
                  </div>
                </div>
                
                {/* Preview Table */}
                <div className="border border-charcoal/10 overflow-hidden mb-4">
                  <div className="overflow-x-auto max-h-96">
                    <table className="w-full text-sm">
                      <thead className="bg-beige sticky top-0">
                        <tr>
                          <th className="p-3 text-left text-xs font-medium text-charcoal-light uppercase">Row</th>
                          <th className="p-3 text-left text-xs font-medium text-charcoal-light uppercase">EAN</th>
                          <th className="p-3 text-left text-xs font-medium text-charcoal-light uppercase">Product</th>
                          <th className="p-3 text-left text-xs font-medium text-charcoal-light uppercase">Size/Color</th>
                          <th className="p-3 text-left text-xs font-medium text-charcoal-light uppercase">Qty</th>
                          <th className="p-3 text-left text-xs font-medium text-charcoal-light uppercase">Stock Change</th>
                          <th className="p-3 text-left text-xs font-medium text-charcoal-light uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-charcoal/10">
                        {importPreview.preview.map((record, idx) => (
                          <tr key={idx} className={
                            record.status === 'invalid' ? 'bg-red-50' :
                            record.status === 'warning' ? 'bg-yellow-50' : ''
                          }>
                            <td className="p-3 text-charcoal-light">{record.row}</td>
                            <td className="p-3 font-mono text-charcoal">{record.ean}</td>
                            <td className="p-3 text-charcoal">{record.product_name || '-'}</td>
                            <td className="p-3 text-charcoal-light">{record.size} / {record.color}</td>
                            <td className="p-3 text-charcoal">{record.quantity}</td>
                            <td className="p-3">
                              {record.status !== 'invalid' && (
                                <span className="text-charcoal">
                                  {record.current_stock} → {record.new_stock}
                                </span>
                              )}
                            </td>
                            <td className="p-3">
                              {record.status === 'valid' && (
                                <span className="inline-flex items-center gap-1 text-green-600">
                                  <CheckCircle className="w-4 h-4" /> Valid
                                </span>
                              )}
                              {record.status === 'warning' && (
                                <span className="inline-flex items-center gap-1 text-yellow-600">
                                  <AlertTriangle className="w-4 h-4" /> {record.message}
                                </span>
                              )}
                              {record.status === 'invalid' && (
                                <span className="inline-flex items-center gap-1 text-red-600">
                                  <XCircle className="w-4 h-4" /> {record.message}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex items-center justify-between pt-4 border-t border-charcoal/10">
                  <button
                    onClick={() => { setImportFile(null); setImportPreview(null); }}
                    className="px-4 py-2 border border-charcoal/20 text-charcoal text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <div className="flex gap-3">
                    {importPreview.warning_records > 0 && (
                      <button
                        onClick={() => processImport(true)}
                        disabled={importing || importPreview.valid_records === 0}
                        className="px-4 py-2 bg-yellow-500 text-white text-sm font-medium hover:bg-yellow-600 disabled:opacity-50"
                      >
                        {importing ? 'Processing...' : `Process All (${importPreview.valid_records + importPreview.warning_records} records)`}
                      </button>
                    )}
                    <button
                      onClick={() => processImport(false)}
                      disabled={importing || importPreview.valid_records === 0}
                      className="px-4 py-2 bg-gold text-white text-sm font-medium hover:bg-gold/90 disabled:opacity-50"
                    >
                      {importing ? 'Processing...' : `Confirm Import (${importPreview.valid_records} valid)`}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Export Tab */}
      {activeTab === 'export' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white border border-charcoal/10 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gold/20 flex items-center justify-center">
                <Download className="w-6 h-6 text-gold" />
              </div>
              <div>
                <h3 className="font-medium text-charcoal">Export Online Sales</h3>
                <p className="text-sm text-charcoal-light">For importing into Vyapar</p>
              </div>
            </div>
            <p className="text-sm text-charcoal-light mb-4">
              Generate Excel file with today&apos;s online sales containing EAN and Quantity Sold for Vyapar import.
            </p>
            <button
              onClick={downloadOnlineSales}
              className="w-full py-3 bg-gold text-white font-medium hover:bg-gold/90"
            >
              Download Online Sales
            </button>
          </div>
          
          <div className="bg-white border border-charcoal/10 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-charcoal/10 flex items-center justify-center">
                <FileSpreadsheet className="w-6 h-6 text-charcoal" />
              </div>
              <div>
                <h3 className="font-medium text-charcoal">Export Master Inventory</h3>
                <p className="text-sm text-charcoal-light">Complete product list with EAN</p>
              </div>
            </div>
            <p className="text-sm text-charcoal-light mb-4">
              Export all products with EAN, SKU, Stock, and Price information for reference or bulk updates.
            </p>
            <button
              onClick={downloadMasterInventory}
              className="w-full py-3 bg-charcoal text-ivory font-medium hover:bg-charcoal/90"
            >
              Download Master Inventory
            </button>
          </div>
        </div>
      )}

      {/* EAN Management Tab */}
      {activeTab === 'ean-management' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="bg-white border border-charcoal/10 p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal-light" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by product name, EAN, or SKU..."
                  className="w-full pl-10 pr-4 py-2.5 border border-charcoal/20 bg-transparent focus:outline-none focus:border-gold text-sm"
                />
              </div>
              <select
                value={eanFilter}
                onChange={(e) => setEanFilter(e.target.value)}
                className="px-4 py-2.5 border border-charcoal/20 bg-transparent focus:outline-none focus:border-gold text-sm min-w-[160px]"
              >
                <option value="all">All Variants</option>
                <option value="with_ean">With EAN</option>
                <option value="without_ean">Without EAN</option>
              </select>
            </div>
          </div>

          {/* EAN List Table */}
          <div className="bg-white border border-charcoal/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-beige">
                  <tr>
                    <th className="p-4 text-left text-xs font-medium text-charcoal-light uppercase">Product</th>
                    <th className="p-4 text-left text-xs font-medium text-charcoal-light uppercase">Size</th>
                    <th className="p-4 text-left text-xs font-medium text-charcoal-light uppercase">Color</th>
                    <th className="p-4 text-left text-xs font-medium text-charcoal-light uppercase">EAN</th>
                    <th className="p-4 text-left text-xs font-medium text-charcoal-light uppercase">SKU</th>
                    <th className="p-4 text-left text-xs font-medium text-charcoal-light uppercase">Stock</th>
                    <th className="p-4 text-left text-xs font-medium text-charcoal-light uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-charcoal/10">
                  {eanList.map((item, idx) => (
                    <tr key={idx} className={!item.ean ? 'bg-yellow-50' : ''}>
                      <td className="p-4">
                        <div>
                          <p className="font-medium text-charcoal line-clamp-1">{item.product_name}</p>
                          <p className="text-xs text-charcoal-light">{item.category}</p>
                        </div>
                      </td>
                      <td className="p-4 text-charcoal">{item.size}</td>
                      <td className="p-4 text-charcoal">{item.color}</td>
                      <td className="p-4">
                        {editingEan === `${item.product_id}_${item.variant_id}` ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={editEanValue}
                              onChange={(e) => setEditEanValue(e.target.value)}
                              className="w-32 px-2 py-1 border border-charcoal/20 text-sm"
                              placeholder="Enter EAN"
                              autoFocus
                            />
                            <button
                              onClick={() => handleUpdateEan(item.product_id, item.variant_id)}
                              className="p-1 text-green-600 hover:bg-green-50"
                            >
                              <Save className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => { setEditingEan(null); setEditEanValue(''); }}
                              className="p-1 text-red-500 hover:bg-red-50"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <span className={`font-mono ${item.ean ? 'text-charcoal' : 'text-red-500'}`}>
                            {item.ean || 'Not Set'}
                          </span>
                        )}
                      </td>
                      <td className="p-4 text-charcoal-light font-mono">{item.sku || '-'}</td>
                      <td className="p-4">
                        <span className={item.stock < 5 ? 'text-red-600 font-medium' : 'text-charcoal'}>
                          {item.stock}
                        </span>
                      </td>
                      <td className="p-4">
                        {editingEan !== `${item.product_id}_${item.variant_id}` && (
                          <button
                            onClick={() => {
                              setEditingEan(`${item.product_id}_${item.variant_id}`);
                              setEditEanValue(item.ean || '');
                            }}
                            className="p-2 text-charcoal-light hover:text-charcoal hover:bg-beige"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {eanList.length === 0 && (
              <div className="p-12 text-center">
                <Package className="w-12 h-12 mx-auto mb-4 text-charcoal-light" />
                <p className="text-charcoal-light">No variants found</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="bg-white border border-charcoal/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-beige">
                <tr>
                  <th className="p-4 text-left text-xs font-medium text-charcoal-light uppercase">Date & Time</th>
                  <th className="p-4 text-left text-xs font-medium text-charcoal-light uppercase">Type</th>
                  <th className="p-4 text-left text-xs font-medium text-charcoal-light uppercase">File</th>
                  <th className="p-4 text-left text-xs font-medium text-charcoal-light uppercase">User</th>
                  <th className="p-4 text-left text-xs font-medium text-charcoal-light uppercase">Records</th>
                  <th className="p-4 text-left text-xs font-medium text-charcoal-light uppercase">Status</th>
                  <th className="p-4 text-left text-xs font-medium text-charcoal-light uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-charcoal/10">
                {history.map((log) => (
                  <tr key={log.id}>
                    <td className="p-4 text-charcoal">{formatDate(log.created_at)}</td>
                    <td className="p-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium ${
                        log.sync_type === 'vyapar_import' 
                          ? 'bg-blue-100 text-blue-700' 
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {log.sync_type === 'vyapar_import' ? (
                          <><Upload className="w-3 h-3" /> Import</>
                        ) : (
                          <><Download className="w-3 h-3" /> Export</>
                        )}
                      </span>
                    </td>
                    <td className="p-4 text-charcoal-light text-xs">{log.file_name || '-'}</td>
                    <td className="p-4 text-charcoal">{log.user_name}</td>
                    <td className="p-4">
                      <span className="text-green-600">{log.successful_records}</span>
                      {log.failed_records > 0 && (
                        <span className="text-red-600"> / {log.failed_records} failed</span>
                      )}
                    </td>
                    <td className="p-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium ${
                        log.status === 'completed' ? 'bg-green-100 text-green-700' :
                        log.status === 'failed' ? 'bg-red-100 text-red-700' :
                        log.status === 'rolled_back' ? 'bg-gray-100 text-gray-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {log.status === 'completed' && <CheckCircle className="w-3 h-3" />}
                        {log.status === 'failed' && <XCircle className="w-3 h-3" />}
                        {log.status === 'rolled_back' && <RotateCcw className="w-3 h-3" />}
                        {log.status.charAt(0).toUpperCase() + log.status.slice(1)}
                      </span>
                    </td>
                    <td className="p-4">
                      {log.can_rollback && log.status === 'completed' && log.sync_type === 'vyapar_import' && (
                        <button
                          onClick={() => handleRollback(log.id)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-charcoal-light hover:text-charcoal hover:bg-beige"
                        >
                          <RotateCcw className="w-3 h-3" />
                          Rollback
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {history.length === 0 && (
            <div className="p-12 text-center">
              <History className="w-12 h-12 mx-auto mb-4 text-charcoal-light" />
              <p className="text-charcoal-light">No sync history yet</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminInventorySync;
